import { setAuthFlash } from "@/lib/auth-flash";
import { API_BASE_URL } from "@/lib/constants";
import { clearTokens, getAccessToken } from "@/lib/auth-storage";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function isPublicAuthPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname === "/sso-callback" ||
    pathname.startsWith("/login/")
  );
}

export function redirectToLogin(message?: string) {
  clearTokens();
  if (typeof window === "undefined") return;
  if (message) setAuthFlash(message);
  if (isPublicAuthPath(window.location.pathname)) return;
  window.location.href = "/login";
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  auth?: boolean;
};

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, auth = true, headers: initHeaders, ...rest } = options;
  const headers = new Headers(initHeaders);

  if (body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Capture the token used for this request so a late 401 from a previous
  // session cannot wipe a freshly issued SSO JWT.
  const requestToken = auth ? getAccessToken() : null;
  if (requestToken) {
    headers.set("Authorization", `Bearer ${requestToken}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401 && auth) {
    if (requestToken && requestToken !== getAccessToken()) {
      throw new ApiError("Unauthorized", 401);
    }
    redirectToLogin("Your session expired. Please sign in again.");
    throw new ApiError("Unauthorized", 401);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof (payload as { error: unknown }).error === "string"
        ? (payload as { error: string }).error
        : typeof payload === "object" &&
            payload !== null &&
            "message" in payload &&
            typeof (payload as { message: unknown }).message === "string"
          ? (payload as { message: string }).message
          : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

/** Multipart upload (no JSON content-type; browser sets the boundary). */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const headers = new Headers();
  const requestToken = getAccessToken();
  if (requestToken) headers.set("Authorization", `Bearer ${requestToken}`);

  const response = await fetch(`${API_BASE_URL}${path}`, { method: "POST", headers, body: formData });
  if (response.status === 401) {
    if (requestToken && requestToken !== getAccessToken()) {
      throw new ApiError("Unauthorized", 401);
    }
    redirectToLogin("Your session expired. Please sign in again.");
    throw new ApiError("Unauthorized", 401);
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload !== null && "error" in payload && typeof (payload as { error: string }).error === "string"
        ? (payload as { error: string }).error
        : `Request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

async function apiFetchFile(path: string): Promise<Response> {
  const headers = new Headers();
  const requestToken = getAccessToken();
  if (requestToken) headers.set("Authorization", `Bearer ${requestToken}`);

  const response = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (response.status === 401) {
    if (requestToken && requestToken !== getAccessToken()) {
      throw new ApiError("Unauthorized", 401);
    }
    redirectToLogin("Your session expired. Please sign in again.");
    throw new ApiError("Unauthorized", 401);
  }
  if (!response.ok) throw new ApiError(`Download failed (${response.status})`, response.status);
  return response;
}

/** Authenticated file download, saved via a temporary object URL. */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await apiFetchFile(path);
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Authenticated blob fetch (e.g. DOCX for in-browser styled preview). */
export async function apiBlob(path: string): Promise<Blob> {
  const response = await apiFetchFile(path);
  return response.blob();
}
